/* ============
 * Create Page
 * ============
 *
 * This is the page for adding bill.
 */
import moment from 'moment';
import { mapGetters } from 'vuex';
import accountService from './../../../../services/account';
import billTypeService from './../../../../services/billType';
import billStatusService from './../../../../services/billStatus';
import orderService from './../../../../services/order';
import settingsService from './../../../../services/organizationSettings';
import billService from './../../../../services/bill';
import billTransformer from './../../../../transformers/bill';
import Forms from './../../../../utils/forms/forms';
import AddOrder from './../../../../pages/withNavigation/orders/create/create.vue';

export default {
  data() {
    return {
      form: new Forms({
        orderId: {
          value: '',
          type: 'autocomplete',
          attributes: {
            isSearch: true,
            searchMethod: this.searchMethod,
            loading: true,
          },
        },
        billStatusId: {
          value: '',
          type: 'hidden',
        },
        date: {
          value: '',
          type: 'date',
          attributes: {
            datePickerOptions: {
              disabledDate: this.disabledDate,
            },
          },
        },
        dueDate: {
          value: '',
          type: 'date',
        },
        languageId: {
          value: '',
          type: 'select',
        },
        currencyId: {
          value: '',
          type: 'select',
        },
        paymentMessage: {
          value: '',
          type: 'textarea',
        },
        note: {
          value: '',
          type: 'textarea',
        },
      }),
      loading: false,
      clientDueDays: null,
      invoiceEdiOrder: true,
    };
  },

  computed: {
    ...mapGetters({
      orders: 'allOrders',
      billTypes: 'billTypes',
      billStatuses: 'billStatuses',
      settings: 'organizationSettings',
      currentOrder: 'currentOrder',
      languages: 'languages',
      currencies: 'currencies',
    }),
  },

  watch: {

    // $route(value) {
    //   console.log(value);
    // },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} billTypes    The billTypes list.
     */
    billTypes(billTypes) {
      this.form.setOptions('billTypeId', billTypes);
    },

    /**
    * Watches state update to inject on Forms class.
    *
    * @param  {Object} orders    The orders list.
    */
    orders(orders) {
      this.form.setOptions('orderId', orders.all.map(o => (
        { id: o.id, name: o.number }
      )));
    },

    /**
     * Watches bill date and add default due days. (initial load)
    *
    * @param  {String} date    The date.
     */
    'form.date': function (date) {
      this.form.dueDate = moment(date).add((this.clientDueDays !== null) ? this.clientDueDays : this.settings.defaultDueDays, 'days').format();
    },

    /**
     * Watches bill date and add default due days. (when selecting order)
    *
    * @param  {String} date    The date.
     */
    clientDueDays(value) {
      this.form.dueDate = moment(this.form.date).add((this.clientDueDays !== null) ? value : this.settings.defaultDueDays, 'days').format();
    },

    languages(value) {
      this.form.setOptions('languageId',
        value.all.map(item => ({
          id: item.id,
          name: item.name,
        })));
    },

    currencies(value) {
      this.form.setOptions('currencyId',
        value.all.map(item => ({
          id: item.id,
          name: item.name,
        })));
    },

    'form.orderId': function (value) {
      let id = 0;
      if (value.id !== undefined) {
        id = value.id;
      } else {
        id = value;
      }
      if (id && id !== 0) {
        accountService.defaultLanguageNCurrency(`order_id=${id}`)
            .then((data) => {
              this.form.setFieldValue('languageId', data.language_id);
              this.form.setFieldValue('currencyId', data.currency_id);
            });
        orderService.show(id);
      }
    },

    currentOrder(value) {
      if (value && value.client) {
        this.clientDueDays = value.client.dueDays;
        this.form.setFieldValue('note', value.referenceNumber || value.client.orderReference);
      }
    },
  },

  created() {
    if (this.$route.query.order) {
      this.invoiceEdiOrder = true;
    } else {
      this.invoiceEdiOrder = false;
    }
    this.form.setQuickForm('orderId', AddOrder);
    const services = [
      billTypeService.all(),
      billStatusService.all(),
      // orderService.all(),
      accountService.languages(),
      accountService.currencies(),
    ];

    if (this.$route.query.order !== undefined) {
      services.push(orderService.clientSetting(this.$route.query.order));
    }

    if (Object.keys(this.settings).length === 0) {
      services.push(settingsService.show());
    }
    this.loading = true;
    Promise.all(services).then(() => {
      if (this.$route.query.order !== undefined) {
        this.form.orderId = this.$route.query.order;
      }
      this.form.date = moment().format();
      this.form.dueDate = moment().add(this.settings.defaultDueDays, 'days').format();
      this.loading = false;
    });
  },

  methods: {
    /**
     * Adds the bill status to the form before saving.
     *
     * @param {String} name The status name.
     */
    save(name) {
      const status = this.billStatuses.filter(s => (
        s.status === name
      ))[0];

      this.form.billStatusId = status.id;

      this.store();
    },

    /**
     * Method to store the bill.
     */
    store() {
      this.form.loading = true;
      const bill = billTransformer.send(this.form.data());
      if (typeof bill.order_id === 'object') {
        bill.order_id = bill.order_id.id;
      }

      billService.store(bill)
      .then((data) => {
        this.$message({
          message: this.$t('static.bills.create.success'),
          type: 'success',
        });
        this.$router.push({
          name: 'bills.edit',
          params: {
            id: data.id,
          },
        });
      }).catch((errors) => {
        if (errors.code === 40116) {
          errors = {
            orderId: [this.$t('static.bills.create.orderError')],
          };
        } else if (errors.ediOrderError) {
          this.$message({
            message: errors.ediOrderError[0],
            type: 'error',
          });
        }

        this.form.loading = false;
        this.form.recordErrors(errors);
      });
    },

    searchMethod(search, field) {
      const obj = {
        type: 'createBill',
        search,
        limit: 10,
      };
      if (!this.invoiceEdiOrder) {
        obj.orderType = 'normal';
      }
      return Promise.all([
        orderService.search(obj),
      ]);
    },

    disabledDate(date) {
      date.setDate(date.getDate() + 15);
      return date < new Date();
    },
  },

  components: {
    VForm: require('./../../../../components/form/form.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/pages/withNavigation/bills/create/create.js